import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Logo, CONTACT } from "@/components/Brand";
import { useForceLightSurface } from "@/context/ThemeContext";

const Shell = ({ title, updated, children, testid }) => {
  useForceLightSurface();
  return (
  <div className="min-h-screen ink text-white" data-testid={testid}>
    <header className="border-b border-white/10">
      <div className="mx-auto max-w-[880px] px-5 sm:px-8 h-16 sm:h-[72px] flex items-center justify-between gap-4">
        <Link to="/" aria-label="AJ Classic Healthcare home"><Logo tone="dark" markClass="h-8 w-8" /></Link>
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white" data-testid="back-home-link">
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>
      </div>
    </header>
    <main className="mx-auto max-w-[880px] px-5 sm:px-8 py-14 sm:py-20">
      <h1 className="font-ui text-3xl sm:text-4xl font-extrabold tracking-[-0.02em]">{title}</h1>
      <p className="mt-3 text-sm text-white/40">Last updated {updated}</p>
      <div className="mt-12 space-y-10">{children}</div>
      <div className="rule-fade my-14" />
      <p className="text-sm text-white/45">
        Questions? Write to <a href={`mailto:${CONTACT.email}`} className="underline decoration-white/30 underline-offset-4 hover:decoration-white">{CONTACT.email}</a>
        {" "}or call {CONTACT.phone} ({CONTACT.hours}).
      </p>
    </main>
  </div>
  );
};

const Block = ({ heading, children }) => (
  <section>
    <h2 className="font-ui text-base md:text-lg font-bold tracking-tight">{heading}</h2>
    <div className="mt-3 space-y-3 text-sm text-white/60 leading-relaxed">{children}</div>
  </section>
);

export function Privacy() {
  return (
    <Shell title="Privacy Policy" updated="June 2026" testid="privacy-page">
      <Block heading="1. Who we are">
        <p>AJ Classic Healthcare provides a multi-tenant Hospital Management System (HMS) to hospitals,
          clinics and pharmacies. Each customer is a separate tenant and controls the clinical and
          business data recorded inside their own workspace.</p>
      </Block>
      <Block heading="2. Data we process">
        <p>On behalf of our customers the platform stores patient demographics and contact details,
          government identity numbers where the hospital chooses to record them, clinical records
          (consultations, prescriptions, admissions, maternity and discharge documents), billing and
          payment records, pharmacy inventory and sales, and staff accounts with attendance.</p>
        <p>For our own operations we process the account details of the users who sign in, security
          logs, and any enquiry you send us by email or WhatsApp.</p>
      </Block>
      <Block heading="3. How data is separated">
        <p>Every record is bound to the tenant that created it. Access is scoped server-side from the
          signed-in session — never from a value supplied by the browser — so one customer can never
          read another customer's data.</p>
      </Block>
      <Block heading="4. Access control and logging">
        <p>Access is granted by role and by individual permission. Sign-in uses server-side sessions
          with httpOnly cookies and brute-force protection. Clinical and financial actions are written
          to an audit log recording the actor, the action and the time.</p>
      </Block>
      <Block heading="5. Identity verification">
        <p>Identity numbers such as Aadhaar, PAN, passport, driving licence, voter ID and GSTIN are
          validated for format, checksum and duplication only. The platform does not verify these
          documents with the issuing authority; that requires a separate authorised integration.</p>
      </Block>
      <Block heading="6. Retention">
        <p>Clinical and financial records are retained by the customer for as long as their own
          regulatory obligations require. Invoices are never deleted — a correction is recorded as a
          void with a reason so the financial history stays intact.</p>
      </Block>
      <Block heading="7. Your rights">
        <p>If you are a patient, the hospital that treated you is the controller of your record —
          please contact them directly to access, correct or erase it. We will support them in
          fulfilling that request.</p>
      </Block>
      <Block heading="8. Contact">
        <p>Email {CONTACT.email} · {CONTACT.phone} · {CONTACT.office}.</p>
      </Block>
    </Shell>
  );
}

export function Terms() {
  return (
    <Shell title="Terms & Conditions" updated="June 2026" testid="terms-page">
      <Block heading="1. Agreement">
        <p>These terms govern use of the AJ Classic Healthcare Hospital Management System. By creating
          a workspace or signing in you accept them on behalf of your organisation.</p>
      </Block>
      <Block heading="2. Accounts and access">
        <p>Workspaces are created for a named business administrator, who is responsible for the staff
          accounts and permissions they issue. Credentials must not be shared. Please tell us
          immediately if you suspect an account has been compromised.</p>
      </Block>
      <Block heading="3. Clinical responsibility">
        <p>The platform records and presents information entered by your staff. It does not provide
          medical advice, diagnosis or treatment decisions. All clinical judgement, and the accuracy of
          what is entered, remains with the treating professionals and your organisation.</p>
      </Block>
      <Block heading="4. Your data">
        <p>You retain ownership of the data in your workspace. We process it only to operate and
          support the service, and to meet our legal obligations.</p>
      </Block>
      <Block heading="5. Acceptable use">
        <p>Do not attempt to access another tenant's data, probe or disrupt the service, upload
          malicious content, or use the platform in breach of applicable healthcare, privacy or tax law.</p>
      </Block>
      <Block heading="6. Third-party services">
        <p>Some features depend on third-party services such as email delivery or messaging providers.
          Where credentials for such a service have not been configured, the related feature will state
          this clearly rather than silently appear to succeed.</p>
      </Block>
      <Block heading="7. Availability and support">
        <p>We aim for continuous availability but do not guarantee uninterrupted service. Support is
          offered during {CONTACT.hours} by email and WhatsApp.</p>
      </Block>
      <Block heading="8. Liability">
        <p>To the maximum extent permitted by law, our liability is limited to the fees paid for the
          service in the twelve months preceding a claim. We are not liable for indirect or
          consequential loss.</p>
      </Block>
      <Block heading="9. Governing law">
        <p>These terms are governed by the laws of India, with courts at Hyderabad, Telangana having
          exclusive jurisdiction.</p>
      </Block>
    </Shell>
  );
}
